import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideLocalizedRoutes } from './app.routes';
import { provideStore } from '@ngxs/store';
import { LOCAL_STORAGE_ENGINE, SESSION_STORAGE_ENGINE, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import * as Sentry from '@sentry/angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApiTokenInterceptor } from '@lexplus-authentication';
import { API_CONFIGURATION, APP_ENVIRONMENT, environmentAdmin } from '@lexplus-environments';
import {
  ColorSchemeService,
  ImpersonationState,
  LOCALIZED_APP_ROUTES,
  ProfileState,
  provideDmLabels,
  SupportWidgetService,
  TemporarySessionState,
} from '@lexplus-shared-lib';
import { AdminLocalizedAppRoutes } from './localized-app-routes';
import { provideDmIcons } from '@digitale-menschen/component-library-angular';
import {
  AlertCircle,
  AlertTriangle,
  Announcement01,
  BarChartSquare02,
  Beaker02,
  Briefcase01,
  CheckCircle,
  ChevronDown,
  Database01,
  DotsHorizontal,
  DownloadCloud02,
  Edit01,
  File04,
  File05,
  FileShield02,
  FilterLines,
  Globe01,
  HelpCircle,
  Image01,
  InfoCircle,
  LayoutLeft,
  Link03,
  LinkExternal01,
  LinkExternal02,
  LogOut01,
  Mail01,
  MarkerPin01,
  Menu02,
  MessageAlertCircle,
  Monitor01,
  Moon01,
  Package,
  PieChart03,
  PlusCircle,
  RefreshCcw01,
  RefreshCw01,
  Scales01,
  Scales02,
  SearchLg,
  Send01,
  Settings02,
  Sun,
  Trash01,
  UploadCloud02,
  User01,
  Users01,
  XCircle,
  XClose,
} from '@digitale-menschen/component-library/untitled-icons';

export const appConfig: ApplicationConfig = {
  providers: [
    // Zoneless is Angular's default; declared explicitly so the app never silently
    // picks up a different change-detection mode from a framework default.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    // The routes themselves come from provideLocalizedRoutes(); route data reaches the
    // component as inputs (isForAdmin and the like).
    provideRouter([], withComponentInputBinding()),
    provideLocalizedRoutes(),
    provideHttpClient(withInterceptorsFromDi()),
    provideDmIcons({
      'alert-circle': AlertCircle,
      'alert-triangle': AlertTriangle,
      'check-circle': CheckCircle,
      'chevron-down': ChevronDown,
      'upload-cloud-02': UploadCloud02,
      'edit-01': Edit01,
      'image-01': Image01,
      'file-05': File05,
      'bar-chart-square-02': BarChartSquare02,
      'scales-01': Scales01,
      'scales-02': Scales02,
      'message-alert-circle': MessageAlertCircle,
      package: Package,
      'briefcase-01': Briefcase01,
      'pie-chart-03': PieChart03,
      'plus-circle': PlusCircle,
      'database-01': Database01,
      'refresh-ccw-01': RefreshCcw01,
      'refresh-cw-01': RefreshCw01,
      'send-01': Send01,
      'settings-02': Settings02,
      'search-lg': SearchLg,
      'layout-left': LayoutLeft,
      'filter-lines': FilterLines,
      'user-01': User01,
      'users-01': Users01,
      'mail-01': Mail01,
      'globe-01': Globe01,
      'moon-01': Moon01,
      'monitor-01': Monitor01,
      sun: Sun,
      'log-out-01': LogOut01,
      'menu-02': Menu02,
      'trash-01': Trash01,
      'dots-horizontal': DotsHorizontal,
      // The Standort Dokumente tab: an external reference's glyph, the date range's clear (×)
      // and the empty state's reset button.
      'link-03': Link03,
      'download-cloud-02': DownloadCloud02,
      'link-external-02': LinkExternal02,
      // The News tab's link column.
      'link-external-01': LinkExternal01,
      'x-close': XClose,
      'x-circle': XCircle,
      // The Newsletter tab's test send, and the info box of the E-Mail-Vorlagen send modal.
      'beaker-02': Beaker02,
      'info-circle': InfoCircle,
      // The nothing-yet states of the lists whose items have no icon of their own in the sidebar.
      'announcement-01': Announcement01,
      'file-04': File04,
      'file-shield-02': FileShield02,
      'help-circle': HelpCircle,
      'marker-pin-01': MarkerPin01,
    }),
    // ImpersonationState is registered so the shared ApiTokenInterceptor can read the
    // impersonation header; the admin app does not impersonate itself (no UI, no
    // persistence) — the state stays empty here.
    provideStore(
      [ProfileState, TemporarySessionState, ImpersonationState],
      withNgxsStoragePlugin({
        keys: [
          { key: ProfileState, engine: LOCAL_STORAGE_ENGINE },
          { key: TemporarySessionState, engine: SESSION_STORAGE_ENGINE },
        ],
      }),
    ),
    // Every dm-* label family the library ships in English; a family left unset renders English.
    provideDmLabels(),
    // The support widget loads on every page, signed in or not: a user who cannot get
    // past the sign-in screen has to be able to report exactly that.
    provideAppInitializer(() => inject(ColorSchemeService).initialize()),
    provideAppInitializer(() => inject(SupportWidgetService).initialize()),
    {
      provide: API_CONFIGURATION,
      useValue: environmentAdmin.apiConfiguration,
    },
    {
      provide: APP_ENVIRONMENT,
      useValue: environmentAdmin,
    },
    {
      provide: LOCALIZED_APP_ROUTES,
      useValue: AdminLocalizedAppRoutes,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiTokenInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
  ],
};
